import { useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LogOut, RefreshCw, Search, FileWarning, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { useAdminData } from "@/hooks/useAdminData";
import { AdminRegistrationsTable } from "@/components/AdminRegistrationsTable";
import { AdminTaxFormsTable } from "@/components/AdminTaxFormsTable";
import { AdminTestimonialsTable } from "@/components/AdminTestimonialsTable";
import { AdminDistributionRequestsTable } from "@/components/AdminDistributionRequestsTable";
import { AdminSubscriptionsTable } from "@/components/AdminSubscriptionsTable";
import { AdminSmartlinksPanel } from "@/components/AdminSmartlinksPanel";

const API = process.env.REACT_APP_BACKEND_URL;
const VALID_TABS = ["registrations", "taxforms", "distribution-requests", "subscriptions", "smartlinks", "testimonials"];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = VALID_TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "registrations";
  const [tab, setTab] = useState(initialTab);
  const [query, setQuery] = useState("");
  const [downloadingCode, setDownloadingCode] = useState(null);
  const [downloadError, setDownloadError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const goToLogin = useCallback(() => navigate("/admin/login", { replace: true }), [navigate]);
  const registrationsData = useAdminData({ onUnauthorized: goToLogin, endpoint: "/api/admin/registrations" });
  const taxFormsData = useAdminData({ onUnauthorized: goToLogin, endpoint: "/api/admin/tax-forms" });
  const testimonialsData = useAdminData({ onUnauthorized: goToLogin, endpoint: "/api/admin/testimonials" });
  const distributionRequestsData = useAdminData({ onUnauthorized: goToLogin, endpoint: "/api/admin/distribution-requests" });
  const subscriptionsData = useAdminData({ onUnauthorized: goToLogin, endpoint: "/api/admin/subscriptions" });
  const { rows, loading, fetchError, fetchRows } =
    tab === "registrations" ? registrationsData
    : tab === "taxforms" ? taxFormsData
    : tab === "testimonials" ? testimonialsData
    : tab === "subscriptions" ? subscriptionsData
    : distributionRequestsData;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    if (tab === "registrations") {
      return rows.filter((r) =>
        [r.name, r.email, r.label, r.code, r.ip_address].some((v) => String(v || "").toLowerCase().includes(q))
      );
    }
    if (tab === "taxforms") {
      return rows.filter((r) =>
        [r.display_name, r.code, r.form_type, r.ip_address].some((v) => String(v || "").toLowerCase().includes(q))
      );
    }
    if (tab === "distribution-requests") {
      return rows.filter((r) =>
        [r.contactName, r.email, r.artistOrLabel, r.code, r.ip_address].some((v) => String(v || "").toLowerCase().includes(q))
      );
    }
    if (tab === "subscriptions") {
      return rows.filter((r) =>
        [r.name, r.email, r.plan, r.interval, r.status].some((v) => String(v || "").toLowerCase().includes(q))
      );
    }
    return rows.filter((r) =>
      [r.name, r.role, r.quote, r.ip_address].some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [rows, query, tab]);

  const handleDeleteTestimonial = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/api/admin/testimonials/${id}`, { withCredentials: true });
      testimonialsData.fetchRows();
    } catch {
      setDownloadError("No se pudo eliminar el testimonio. Intenta de nuevo.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (code) => {
    setDownloadingCode(code);
    setDownloadError("");
    try {
      const path = tab === "registrations"
        ? `/api/admin/registrations/${code}/pdf`
        : `/api/admin/tax-forms/${code}/pdf`;
      const res = await axios.get(`${API}${path}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${code}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setDownloadError(`No se pudo descargar el PDF de ${code}. Intenta de nuevo.`);
    } finally {
      setDownloadingCode(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    goToLogin();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-4 sm:px-8 py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold">Panel de Distribución</h1>
            <p className="text-neutral-500 text-sm mt-0.5">{admin?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchRows}
              data-testid="admin-refresh-button"
              className="h-10 w-10 flex items-center justify-center rounded-full border border-white/10 hover:bg-white/5 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="h-10 px-4 flex items-center gap-2 rounded-full border border-white/10 hover:bg-white/5 transition-colors text-sm font-medium"
            >
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <button
            data-testid="admin-tab-registrations"
            onClick={() => { setTab("registrations"); setQuery(""); }}
            className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${
              tab === "registrations" ? "bg-brand-red text-white" : "bg-white/5 text-neutral-400 hover:bg-white/10"
            }`}
          >
            Acuerdos firmados
          </button>
          <button
            data-testid="admin-tab-taxforms"
            onClick={() => { setTab("taxforms"); setQuery(""); }}
            className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${
              tab === "taxforms" ? "bg-brand-red text-white" : "bg-white/5 text-neutral-400 hover:bg-white/10"
            }`}
          >
            Formularios fiscales
          </button>
          <button
            data-testid="admin-tab-testimonials"
            onClick={() => { setTab("testimonials"); setQuery(""); }}
            className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${
              tab === "testimonials" ? "bg-brand-red text-white" : "bg-white/5 text-neutral-400 hover:bg-white/10"
            }`}
          >
            Testimonios
          </button>
          <button
            data-testid="admin-tab-distribution-requests"
            onClick={() => { setTab("distribution-requests"); setQuery(""); }}
            className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${
              tab === "distribution-requests" ? "bg-brand-red text-white" : "bg-white/5 text-neutral-400 hover:bg-white/10"
            }`}
          >
            Solicitudes de distribución
          </button>
          <button
            data-testid="admin-tab-subscriptions"
            onClick={() => { setTab("subscriptions"); setQuery(""); }}
            className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${
              tab === "subscriptions" ? "bg-brand-red text-white" : "bg-white/5 text-neutral-400 hover:bg-white/10"
            }`}
          >
            Suscripciones
          </button>
          <button
            data-testid="admin-tab-smartlinks"
            onClick={() => { setTab("smartlinks"); setQuery(""); }}
            className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${
              tab === "smartlinks" ? "bg-brand-red text-white" : "bg-white/5 text-neutral-400 hover:bg-white/10"
            }`}
          >
            Smartlinks
          </button>
        </div>

        {tab === "smartlinks" ? (
          <AdminSmartlinksPanel onUnauthorized={goToLogin} />
        ) : (
          <>
            <div className="relative mb-6 max-w-sm">
              <Search className="w-4 h-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="admin-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre, email, código..."
                className="bg-white/[0.03] border-white/10 text-white h-10 pl-9"
              />
            </div>

            {downloadError && (
              <div data-testid="admin-download-error" className="flex items-start gap-2 rounded-lg border border-brand-red/30 bg-brand-red/[0.06] p-3 mb-4 max-w-md">
                <AlertCircle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
                <p className="text-neutral-300 text-xs leading-relaxed">{downloadError}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-20 text-neutral-500 gap-2" data-testid="admin-loading">
                <Loader2 className="w-5 h-5 animate-spin" /> Cargando registros...
              </div>
            ) : fetchError ? (
              <div className="flex flex-col items-center justify-center py-20 text-brand-red gap-2" data-testid="admin-fetch-error">
                <AlertCircle className="w-6 h-6" />
                <p className="text-sm">{fetchError}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-neutral-500 gap-2" data-testid="admin-empty-state">
                <FileWarning className="w-6 h-6" />
                <p className="text-sm">No hay registros que coincidan.</p>
              </div>
            ) : tab === "registrations" ? (
              <AdminRegistrationsTable rows={filtered} downloadingCode={downloadingCode} onDownload={handleDownload} />
            ) : tab === "taxforms" ? (
              <AdminTaxFormsTable rows={filtered} downloadingCode={downloadingCode} onDownload={handleDownload} />
            ) : tab === "distribution-requests" ? (
              <AdminDistributionRequestsTable rows={filtered} />
            ) : tab === "subscriptions" ? (
              <AdminSubscriptionsTable rows={filtered} />
            ) : (
              <AdminTestimonialsTable rows={filtered} deletingId={deletingId} onDelete={handleDeleteTestimonial} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
